import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Shield } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: formData.username,
        password: formData.password
      });
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('username', response.data.username);
      localStorage.setItem('role', response.data.role);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
      console.error('Auth error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <Toaster position="top-right" richColors />
      
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
      <div className="absolute top-20 left-20 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-red-600/10 rounded-full blur-3xl"></div>

      {/* Logo */}
      <div className="absolute top-8 left-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_safefire/artifacts/vcg3q2eg_path1000.png" 
          alt="Mertek Logo" 
          className="h-10 w-auto"
        />
      </div>

      <Card data-testid="admin-login-card" className="relative z-10 w-full max-w-md bg-white/5 backdrop-blur-xl border-white/10">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto bg-orange-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-orange-500" />
          </div>
          <CardTitle className="text-3xl font-bold text-white">
            Admin Login
          </CardTitle>
          <p className="text-gray-400">
            Access your dashboard
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-gray-300">Username</Label>
              <Input
                data-testid="admin-username-input"
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                placeholder="Enter username"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Input
                data-testid="admin-password-input"
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                placeholder="Enter password"
              />
            </div>

            <Button
              data-testid="admin-submit-btn"
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white py-6 border-0"
            >
              {isSubmitting ? 'Processing...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              data-testid="back-to-home-btn"
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;